import { useEffect, useState } from 'react';
import { api, Avatar, cn, useParty } from '@karozik/ui';
import type { AdminEvent } from '../types';

interface GuestRow {
  id: string;
  pseudo: string;
  emoji: string;
  color: string;
  banned: boolean;
  lastSeenAt: number;
  played: number;
  reactions: number;
  hasFace: boolean;
  faceLandmarks: boolean;
}

export function GuestsTab({ event }: { event: AdminEvent }) {
  const [guests, setGuests] = useState<GuestRow[]>([]);
  const conn = useParty({ role: 'admin', eventId: event.id }, { keepReactions: false });
  const load = () => void api<GuestRow[]>(`/api/admin/events/${event.id}/guests`).then(setGuests);
  useEffect(load, [event.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <p className="mb-3 text-sm text-ink-dim">
        {guests.length} invités inscrits · {guests.filter((g) => g.hasFace).length} selfies pour Têtes Mêlées (gardés en mémoire jusqu’à la fin de la soirée).
      </p>
      <ul className="space-y-2">
        {guests.map((g) => (
          <li key={g.id} className={cn('flex items-center gap-3 rounded-2xl bg-surface p-3', g.banned && 'opacity-50')}>
            {g.hasFace ? (
              <img src={`/api/admin/events/${event.id}/faces/${g.id}`} alt={`Selfie de ${g.pseudo}`} className="h-12 w-[2.4rem] shrink-0 rounded-lg object-cover" />
            ) : (
              <Avatar guest={g} />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{g.pseudo}</div>
              <div className="text-xs text-ink-faint">
                {g.played} titres joués · {g.reactions} réactions · vu à {new Date(g.lastSeenAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                {g.hasFace && (g.faceLandmarks ? ' · ✨ visage repéré' : ' · visage non repéré (pas de morphing)')}
              </div>
            </div>
            {g.hasFace && !g.banned && (
              <button
                onClick={async () => {
                  if (!confirm(`Retirer le selfie de ${g.pseudo} ?`)) return;
                  await api(`/api/admin/events/${event.id}/faces/${g.id}`, { method: 'DELETE' });
                  load();
                }}
                className="rounded-xl bg-surface-2 px-3 py-1.5 text-sm"
              >
                Retirer le selfie
              </button>
            )}
            {g.banned ? (
              <span className="text-xs text-danger">Banni</span>
            ) : (
              <button
                onClick={async () => {
                  if (!confirm(`Bannir ${g.pseudo} ? Ses titres en attente seront retirés.`)) return;
                  await conn.send('control', { type: 'banGuest', guestId: g.id });
                  load();
                }}
                className="rounded-xl bg-surface-2 px-3 py-1.5 text-sm"
              >
                Bannir
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
